import { useEffect, useState } from 'react';
import { Zap, Crown, Key, Shield } from 'lucide-react';

/**
 * Indice flotante (TOC) para la pagina /planes-y-beneficios.
 * - Solo visible en desktop (lg+).
 * - Posicion fija a la derecha, debajo del header sticky.
 * - Highlights dinamicos segun la seccion visible (IntersectionObserver).
 * - Click hace scroll suave al data-testid de cada seccion.
 */
const SECTIONS = [
  { id: 'free-plan-section',       icon: Zap,    color: 'cyan',    label: { es: 'Plan Free',       en: 'Free Plan' } },
  { id: 'premium-plan-section',    icon: Crown,  color: 'amber',   label: { es: 'Plan Premium',    en: 'Premium Plan' } },
  { id: 'advanced-plan-section',   icon: Key,    color: 'emerald', label: { es: 'Historial Avanzado',   en: 'Advanced History' } },
  { id: 'prohibitions-section',    icon: Shield, color: 'rose',    label: { es: 'Reglas',          en: 'Rules' } },
];

const COLOR_CLASSES = {
  cyan:    { active: 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300',       idle: 'text-slate-400 hover:text-cyan-300' },
  amber:   { active: 'bg-amber-500/15 border-amber-500/40 text-amber-300',    idle: 'text-slate-400 hover:text-amber-300' },
  emerald: { active: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300', idle: 'text-slate-400 hover:text-emerald-300' },
  rose:    { active: 'bg-rose-500/15 border-rose-500/40 text-rose-300',       idle: 'text-slate-400 hover:text-rose-300' },
};

export default function PricingTOC({ language = 'es' }) {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    const targets = SECTIONS
      .map((s) => document.querySelector(`[data-testid="${s.id}"]`))
      .filter(Boolean);
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const id = visible.target.getAttribute('data-testid');
          if (id) setActiveId(id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  return (
    <nav
      className="hidden lg:flex flex-col gap-1.5 p-3 rounded-2xl bg-slate-900/70 backdrop-blur-md
                 border border-slate-700/60 shadow-2xl shadow-black/40
                 fixed top-24 right-6 w-52 z-40"
      aria-label={language === 'es' ? 'Indice de secciones' : 'Sections index'}
      data-testid="pricing-toc"
    >
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 pb-1 border-b border-slate-700/50">
        {language === 'es' ? 'En esta página' : 'On this page'}
      </p>
      {SECTIONS.map((s) => {
        const Icon = s.icon;
        const isActive = activeId === s.id;
        const palette = COLOR_CLASSES[s.color];
        return (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={(e) => handleClick(e, s.id)}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all
                        ${isActive ? `${palette.active} border` : `${palette.idle} border-transparent`}`}
            data-testid={`toc-link-${s.id}`}
          >
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <span>{s.label[language] || s.label.es}</span>
          </a>
        );
      })}
    </nav>
  );
}
